import { useState, useEffect, useMemo, useRef } from "react";
import { NavLink } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import { useAuth } from "../FIREBASE/useAuth";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { WorldMap, Country, loadCountryList } from "../COMPONENTS/places/WorldMap";
import { Globe } from "../COMPONENTS/places/Globe";
import { EqualEarthMap } from "../COMPONENTS/places/EqualEarthMap";
import { ukNationCode, UK_NATIONS } from "../COMPONENTS/places/ukSubunits";
import { geocode, GeoResult } from "../COMPONENTS/planner/geocode";
import "../CSS/places.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

// Continents used for the per-continent breakdown, in display order.
const CONTINENTS = ["Europe", "Asia", "Africa", "North America", "South America", "Oceania", "Antarctica"];
const CONTINENT_SV: Record<string, string> = {
  Europe: "Europa",
  Asia: "Asien",
  Africa: "Afrika",
  "North America": "Nordamerika",
  "South America": "Sydamerika",
  Oceania: "Oceanien",
  Antarctica: "Antarktis",
};

interface Place {
  id: string; // "lat,lng" (4 dp) — stable identity for dedupe/remove
  name: string; // full label, e.g. "Funäsdalen, Härjedalen, Sverige"
  lat: number;
  lng: number;
  cc: string; // ISO-2 country code (uppercase), "" if Photon gave none
  country: string; // localized country name for grouping headers
  date?: string; // free-text "when I was here" (e.g. "2023" or "jul 2023")
  trip?: string; // trip label — places sharing it form one chained journey
}

const placeId = (lat: number, lng: number) => `${lat.toFixed(4)},${lng.toFixed(4)}`;

// Rough flight-distance model: home hub (Stockholm) + Earth's circumference.
const HOME: [number, number] = [59.33, 18.06];
const HOME_CC = "SE";
const EARTH_CIRC_KM = 40075;
const haversineKm = (a: [number, number], b: [number, number]): number => {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

// UK places report cc "GB"; resolve each to its constituent nation (GB-ENG,
// GB-SCT, …) so England/Scotland/Wales/N. Ireland count and fill separately.
// Also upgrades places saved before the UK split (still stored as plain "GB").
const resolveUkPlaces = (list: Place[]): Promise<Place[]> =>
  Promise.all(
    list.map(async (p) => {
      const q = { ...p, date: p.date || "", trip: p.trip || "" }; // defaults so Firestore never sees undefined
      if (q.cc !== "GB") return q;
      const nation = await ukNationCode(q.lat, q.lng);
      return nation ? { ...q, cc: nation, country: UK_NATIONS[nation]?.name || q.country } : q;
    })
  );

// Debounced Photon autocomplete; calls onAdd with the chosen place.
function PlaceSearch({ onAdd }: { onAdd: (p: Place) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GeoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await geocode(q, ctrl.signal);
        setResults(res);
        setOpen(true);
      } catch {
        /* aborted or failed — leave prior results */
      }
      setSearching(false);
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  // close the dropdown on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (r: GeoResult) => {
    onAdd({
      id: placeId(r.lat, r.lng),
      name: r.label,
      lat: r.lat,
      lng: r.lng,
      cc: (r.countrycode || "").toUpperCase(),
      country: r.country || "",
      date: "",
      trip: "",
    });
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="places__search-box" ref={boxRef}>
      <input
        className="places__search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Sök stad eller plats…"
      />
      {open && (results.length > 0 || searching) && (
        <ul className="places__results">
          {searching && results.length === 0 && (
            <li className="places__result places__result--muted">Söker…</li>
          )}
          {results.map((r) => (
            <li
              key={`${r.lat},${r.lng}`}
              className="places__result"
              onClick={() => pick(r)}
            >
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const PlacesView = () => {
  usePageBackground();
  const { isAdmin } = useAuth();
  const [places, setPlaces] = useState<Place[]>([]);
  const [saved, setSaved] = useState<Place[]>([]); // last persisted, for dirty-check
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeYear, setActiveYear] = useState<number | null>(null); // year-chart popover
  const [flightInfoOpen, setFlightInfoOpen] = useState(false); // flight-stat popover (mobile tap)
  const [openContinent, setOpenContinent] = useState<string | null>(null); // continent country list
  const chartRef = useRef<HTMLDivElement>(null);
  const flightCardRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<"map" | "equal" | "globe">(() => {
    const v = localStorage.getItem("places-view");
    return v === "globe" || v === "equal" ? v : "map";
  });

  // close the tap-popovers when tapping/clicking outside them
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (chartRef.current && !chartRef.current.contains(t)) setActiveYear(null);
      if (flightCardRef.current && !flightCardRef.current.contains(t)) setFlightInfoOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const chooseView = (v: "map" | "equal" | "globe") => {
    setView(v);
    localStorage.setItem("places-view", v);
  };

  useEffect(() => {
    db.collection("meta")
      .doc("places")
      .get()
      .then(async (snap) => {
        const raw: Place[] = (snap.data()?.places as Place[]) ?? [];
        // Normalize legacy "GB" places to their UK nation so England fills.
        const list = await resolveUkPlaces(raw);
        setPlaces(list);
        setSaved(list);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Country list (for the stats) loads independently of the map/globe view.
  useEffect(() => {
    loadCountryList()
      .then((list: Country[]) => setCountries(list))
      .catch(() => {});
  }, []);

  // Countries counted as visited = those with at least one saved place.
  const visitedCC = useMemo(
    () => new Set(places.map((p) => p.cc).filter(Boolean)),
    [places]
  );
  const visitedList = useMemo(() => Array.from(visitedCC), [visitedCC]);
  const mapPlaces = useMemo(
    () => places.map((p) => ({ name: p.name, lat: p.lat, lng: p.lng, date: p.date || "" })),
    [places]
  );

  // Countries visited per year, parsed from each place's free-text date. A
  // country counts once per year it appears in; years with no data show empty.
  const yearStats = useMemo(() => {
    const byYear = new Map<number, Map<string, string>>(); // year → cc → country name
    places.forEach((p) => {
      if (!p.cc) return;
      // a place can list several visits ("2019, 2023") — count each year
      const found = (p.date || "").match(/\b(?:19|20)\d{2}\b/g);
      if (!found) return;
      found.forEach((ys) => {
        const y = Number(ys);
        if (!byYear.has(y)) byYear.set(y, new Map());
        byYear.get(y)!.set(p.cc, p.country || p.cc);
      });
    });
    if (byYear.size === 0) return null;
    const keys = Array.from(byYear.keys());
    const min = Math.min(...keys);
    const max = Math.max(...keys);
    const years: { year: number; count: number; countries: string[] }[] = [];
    for (let y = min; y <= max; y++) {
      const ccMap = byYear.get(y);
      years.push({
        year: y,
        count: ccMap?.size ?? 0,
        countries: ccMap ? Array.from(ccMap.values()).sort((a, b) => a.localeCompare(b, "sv")) : [],
      });
    }
    const maxCount = Math.max(...years.map((y) => y.count), 1);
    return { years, maxCount };
  }, [places]);

  // Rough "laps around the Earth flown": one round trip from home per distinct
  // country per year (same country+year ⇒ counted once), summed and divided by
  // the Earth's circumference. Domestic (Sweden) and undated places are skipped.
  const flightStats = useMemo(() => {
    // Nearest-neighbour ordering of destination coords, starting from home.
    const orderFromHome = (coords: [number, number][]): [number, number][] => {
      const remaining = [...coords];
      const out: [number, number][] = [];
      let cur: [number, number] = HOME;
      while (remaining.length) {
        let bi = 0;
        let bd = Infinity;
        remaining.forEach((c, i) => {
          const d = haversineKm(cur, c);
          if (d < bd) { bd = d; bi = i; }
        });
        cur = remaining[bi];
        out.push(cur);
        remaining.splice(bi, 1);
      }
      return out;
    };

    let km = 0;

    // Linked trips: one distinct coord per country, chained home → … → home.
    const tripGroups = new Map<string, Place[]>();
    const singles: Place[] = [];
    places.forEach((p) => {
      if (!p.cc || p.cc === HOME_CC) return;
      if (p.trip && p.trip.trim()) {
        const k = p.trip.trim();
        if (!tripGroups.has(k)) tripGroups.set(k, []);
        tripGroups.get(k)!.push(p);
      } else {
        singles.push(p);
      }
    });

    tripGroups.forEach((group) => {
      const byCc = new Map<string, [number, number]>();
      group.forEach((p) => { if (!byCc.has(p.cc)) byCc.set(p.cc, [p.lat, p.lng]); });
      const ordered = orderFromHome(Array.from(byCc.values()));
      if (ordered.length === 0) return;
      const path: [number, number][] = [HOME, ...ordered, HOME];
      for (let i = 0; i < path.length - 1; i++) km += haversineKm(path[i], path[i + 1]);
    });

    // Standalone places: round trip from home per (year|country).
    const seen = new Map<string, [number, number]>();
    singles.forEach((p) => {
      const years = (p.date || "").match(/\b(?:19|20)\d{2}\b/g);
      if (!years) return;
      years.forEach((y) => {
        const key = `${y}|${p.cc}`;
        if (!seen.has(key)) seen.set(key, [p.lat, p.lng]);
      });
    });
    seen.forEach((coord) => { km += 2 * haversineKm(HOME, coord); });

    if (km <= 0) return null;
    return { km, laps: km / EARTH_CIRC_KM, tripCount: tripGroups.size };
  }, [places]);

  const addPlace = async (p: Place) => {
    if (!isAdmin || !editing) return;
    let place = p;
    // UK places all report cc "GB"; resolve to the constituent country so
    // England/Scotland/Wales/N. Ireland count and fill separately.
    if (p.cc === "GB") {
      const nation = await ukNationCode(p.lat, p.lng);
      if (nation) place = { ...p, cc: nation, country: UK_NATIONS[nation]?.name || p.country };
    }
    setPlaces((prev) => (prev.some((x) => x.id === place.id) ? prev : [...prev, place]));
  };
  const removePlace = (id: string) =>
    setPlaces((prev) => prev.filter((p) => p.id !== id));
  const setPlaceDate = (id: string, date: string) =>
    setPlaces((prev) => prev.map((p) => (p.id === id ? { ...p, date } : p)));
  const setPlaceTrip = (id: string, trip: string) =>
    setPlaces((prev) => prev.map((p) => (p.id === id ? { ...p, trip } : p)));

  const dirty = useMemo(() => {
    if (places.length !== saved.length) return true;
    const sig = (p: Place) => `${p.date || ""}|${p.trip || ""}`;
    const sd = new Map(saved.map((p) => [p.id, sig(p)]));
    return places.some((p) => !sd.has(p.id) || sd.get(p.id) !== sig(p));
  }, [places, saved]);

  const save = async () => {
    setSaving(true);
    try {
      await db.collection("meta").doc("places").set({ places }, { merge: true });
      setSaved(places);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch {
      /* leave dirty so the user can retry */
    }
    setSaving(false);
  };

  const stats = useMemo(() => {
    const total = countries.length;
    const visitedCount = countries.filter((c) => visitedCC.has(c.code)).length;
    const byContinent = CONTINENTS.map((cont) => {
      const inCont = countries.filter((c) => c.continent === cont);
      return {
        cont,
        total: inCont.length,
        visited: inCont.filter((c) => visitedCC.has(c.code)).length,
      };
    });
    return { total, visitedCount, byContinent };
  }, [countries, visitedCC]);

  const pct = (v: number, t: number) => (t > 0 ? Math.round((v / t) * 100) : 0);

  // Saved places grouped by country for the editor list.
  const grouped = useMemo(() => {
    const map = new Map<string, { country: string; items: Place[] }>();
    places.forEach((p) => {
      const key = p.cc || "??";
      if (!map.has(key)) map.set(key, { country: p.country || p.cc || "Okänt", items: [] });
      map.get(key)!.items.push(p);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.country.localeCompare(b.country, "sv")
    );
  }, [places]);

  return (
    <div className="places">
      <div className="places__header">
        <NavLink to={routeUrls.BLOGG} className="places__back">← Resor</NavLink>
        <h1 className="places__title">Platser jag besökt</h1>
        {isAdmin && (
          <button
            className="places__edit-toggle"
            onClick={() => {
              if (editing && dirty) setPlaces(saved); // discard unsaved edits on leave
              setEditing((e) => !e);
            }}
          >
            {editing ? "Klar" : "Redigera"}
          </button>
        )}
      </div>

      <div className="places__stats">
        <div className="places__stat places__stat--big">
          <span className="places__stat-num">{stats.visitedCount}</span>
          <span className="places__stat-label">länder besökta</span>
        </div>
        <div className="places__stat places__stat--big">
          <span className="places__stat-num">{places.length}</span>
          <span className="places__stat-label">platser</span>
        </div>
        <div className="places__stat places__stat--big">
          <span className="places__stat-num">{pct(stats.visitedCount, stats.total)}%</span>
          <span className="places__stat-label">av världen</span>
        </div>
        {stats.byContinent.map((c) => (
          <button
            key={c.cont}
            className="places__stat places__stat--btn"
            onClick={() => setOpenContinent(c.cont)}
            title={`Visa länder i ${CONTINENT_SV[c.cont]}`}
          >
            <span className="places__stat-num">
              {c.visited}
              <span className="places__stat-of">/{c.total}</span>
            </span>
            <span className="places__stat-label">
              {CONTINENT_SV[c.cont]} · {pct(c.visited, c.total)}%
            </span>
          </button>
        ))}
        {flightStats && (
          <div
            ref={flightCardRef}
            className={`places__stat places__stat--big places__stat--info${flightInfoOpen ? " places__stat--open" : ""}`}
            onPointerDown={(e) => {
              if (e.pointerType !== "mouse") setFlightInfoOpen((v) => !v);
            }}
          >
            <span className="places__stat-num">{flightStats.laps.toFixed(1)}</span>
            <span className="places__stat-label">✈️ varv runt jorden</span>
            <div className="places__stat-pop">
              ≈ {Math.round(flightStats.km).toLocaleString("sv-SE")} km flygresor (uppskattat från
              Stockholm; {flightStats.tripCount} länkade resor kedjas)
            </div>
          </div>
        )}
      </div>

      <div className="places__view-toggle">
        <button
          className={`places__view-btn${view === "map" ? " places__view-btn--active" : ""}`}
          onClick={() => chooseView("map")}
        >
          🗺️ Karta
        </button>
        <button
          className={`places__view-btn${view === "equal" ? " places__view-btn--active" : ""}`}
          onClick={() => chooseView("equal")}
        >
          🌐 Equal Earth
        </button>
        <button
          className={`places__view-btn${view === "globe" ? " places__view-btn--active" : ""}`}
          onClick={() => chooseView("globe")}
        >
          🌍 Glob
        </button>
      </div>

      {view === "globe" ? (
        <Globe visited={visitedList} places={mapPlaces} />
      ) : view === "equal" ? (
        <EqualEarthMap visited={visitedList} places={mapPlaces} />
      ) : (
        <WorldMap visited={visitedList} places={mapPlaces} />
      )}

      {yearStats && (
        <div className="places__year-chart" ref={chartRef}>
          <h3 className="places__year-title">Länder besökta per år</h3>
          <div className="places__year-plot">
            {yearStats.years.map((y) => (
              <div
                key={y.year}
                className={`places__year-col${activeYear === y.year ? " places__year-col--active" : ""}`}
                onMouseEnter={() => y.count > 0 && setActiveYear(y.year)}
                onMouseLeave={() => setActiveYear((cur) => (cur === y.year ? null : cur))}
                onPointerDown={(e) => {
                  if (e.pointerType !== "mouse" && y.count > 0)
                    setActiveYear((cur) => (cur === y.year ? null : y.year));
                }}
              >
                {activeYear === y.year && y.countries.length > 0 && (
                  <div className="places__year-pop">
                    <div className="places__year-pop-head">{y.year} · {y.count} länder</div>
                    <ul className="places__year-pop-list">
                      {y.countries.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <span className="places__year-count">{y.count || ""}</span>
                <div
                  className="places__year-bar"
                  style={{ height: `${(y.count / yearStats.maxCount) * 100}%` }}
                />
              </div>
            ))}
          </div>
          <div className="places__year-labels">
            {yearStats.years.map((y) => (
              <span key={y.year} className="places__year-label">{`'${String(y.year).slice(2)}`}</span>
            ))}
          </div>
        </div>
      )}

      {isAdmin && editing && (
        <div className="places__editor">
          <div className="places__editor-head">
            <p className="places__editor-hint">
              Sök efter en stad eller plats och lägg till den. Landet räknas som
              besökt så snart du lagt till minst en plats där.
            </p>
            <div className="places__editor-actions">
              {dirty && (
                <button className="places__revert" onClick={() => setPlaces(saved)}>
                  Ångra
                </button>
              )}
              <button className="places__save" onClick={save} disabled={saving || !dirty}>
                {saving ? "Sparar…" : "Spara"}
              </button>
              {savedMsg && <span className="places__saved">Sparat!</span>}
            </div>
          </div>

          <PlaceSearch onAdd={addPlace} />

          {places.length === 0 ? (
            <p className="places__empty">Inga platser tillagda ännu.</p>
          ) : (
            grouped.map((group) => (
              <div key={group.country} className="places__group">
                <p className="places__group-label">
                  {group.country} · {group.items.length}
                </p>
                <div className="places__place-list">
                  {group.items.map((p) => (
                    <div key={p.id} className="places__place">
                      <span className="places__place-name">{p.name}</span>
                      <input
                        className="places__place-date"
                        value={p.date || ""}
                        onChange={(e) => setPlaceDate(p.id, e.target.value)}
                        placeholder="När? (t.ex. 2019, 2023)"
                      />
                      <input
                        className="places__place-trip"
                        value={p.trip || ""}
                        onChange={(e) => setPlaceTrip(p.id, e.target.value)}
                        placeholder="Resa (länka ihop)"
                      />
                      <button
                        className="places__place-remove"
                        onClick={() => removePlace(p.id)}
                        aria-label="Ta bort plats"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {loading && <p className="places__loading">Laddar…</p>}

      {openContinent && (
        <div className="places__modal-overlay" onClick={() => setOpenContinent(null)}>
          <div className="places__modal" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const inCont = countries.filter((c) => c.continent === openContinent);
              const visitedN = inCont.filter((c) => visitedCC.has(c.code)).length;
              return (
                <>
                  <div className="places__modal-head">
                    <h3 className="places__modal-title">{CONTINENT_SV[openContinent] || openContinent}</h3>
                    <span className="places__modal-sub">{visitedN}/{inCont.length} besökta</span>
                    <button
                      className="places__modal-close"
                      onClick={() => setOpenContinent(null)}
                      aria-label="Stäng"
                    >
                      ×
                    </button>
                  </div>
                  <ul className="places__country-list">
                    {inCont.map((c) => {
                      const been = visitedCC.has(c.code);
                      return (
                        <li
                          key={c.code}
                          className={`places__country-item${been ? " places__country-item--visited" : ""}`}
                        >
                          <span className="places__country-check">{been ? "✓" : ""}</span>
                          {c.name}
                        </li>
                      );
                    })}
                  </ul>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
